import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { useSnackbar } from 'notistack';
import { isMobile, isSafari, isIOS } from 'react-device-detect';
import {
    Box, Button, DialogActions, Fab, Grid,
    IconButton, ImageList,
    ImageListItem, Tab, Tabs,
    TextareaAutosize, Typography,
} from '@mui/material';
import { AddPhotoAlternate, Cached, Save } from '@mui/icons-material';
import { a11yProps, TabPanels } from 'components/mui/Tabs';
import PhotoMaker from 'components/PhotoMaker/PhotoMaker';
import WebCamera from 'components/PhotoMaker/WebCamera';
import useSwiperTabs from 'hooks/useSwiperTabs';
import { ModalActionProps } from 'models/AutoModals';
import { CarModel, StockStatus } from 'models/StockModel';
import useCarPhotos from 'modules/cars/hooks/useCarPhotos';
import { resizeImage } from 'modules/cars/utils/common';
import photoPlace from 'recoil/atoms/photoPlace';


type PhotoAdderProps = {
    car: CarModel,
} & ModalActionProps

const albumsByPlace = {
    'yard': ['note', 'cargo', 'problems'],
    'vessel': ['note'],
    'destination': ['note', 'problems'],
} as { [k: string]: (keyof typeof tabLabelByAlbum)[] };

const albumsSquashed: string[] = [];
Object.entries(albumsByPlace).forEach(([p, v]) => v.forEach(a => albumsSquashed.push(`${p}_${a}`)))

const tabLabelByAlbum = {
    'note': 'Photos',
    'cargo': 'Inner cargo',
    'problems': 'Problems',
}

const PhotoAdder = ({car, onFinish}: PhotoAdderProps) => {
    const {t} = useTranslation('cars');
    const {addPhotos} = useCarPhotos();
    const {enqueueSnackbar} = useSnackbar();
    const [place, setPlace] = useRecoilState(photoPlace);
    const [texts, setTexts] = useState(Object.fromEntries(car.albums.map(a => [a.name, a.note])));
    const [newPhotos, setNewPhotos] = useState(Object.fromEntries(albumsSquashed.map(a => [a, [] as File[]])));

    useEffect(() => {
        setPlace(old => {
            if (car.status === StockStatus.Shipped)
                return 'destination';

            if (old === 'destination')
                return '';

            return old;
        });
        // eslint-disable-next-line
    }, []);

    const save = () => {
        const albums = Object.entries(newPhotos)
            .filter(([a, p]) => p.length || texts[a]) // has photos or text
            .map(([a, p]) => ({
                name: a,
                text: texts[a],
                photos: p,
            }));

        if (!albums.length) return;

        addPhotos(car.id, albums)
            .then(() => {
                enqueueSnackbar(t('saved'), {variant: 'success', autoHideDuration: 1000});
                setNewPhotos(Object.fromEntries(albumsSquashed.map(a => [a, []])));

                onFinish(null);
            });
    };

    return (
        <>
            <Box sx={{p: 2, position: 'relative'}}>
                {(car.status === StockStatus.Pending || car.status === StockStatus.Ready) &&
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: (theme) => theme.spacing(1),
                            right: (theme) => theme.spacing(1)
                        }}
                        onClick={() => setPlace(old => old === 'yard' ? 'vessel' : 'yard')}
                    >
                        <Cached/>
                    </IconButton>
                }

                <Typography component="h2" variant="h5" gutterBottom>
                    {car.chassisNumber}
                </Typography>

                <Typography>
                    {car.make} {car.model} - {car.company.name}
                </Typography>

                <Grid container justifyContent="space-between">
                    <Grid>
                        {car.yard?.port}
                    </Grid>
                    <Grid>
                        Location: {car.yard?.location || 'unknown'}
                    </Grid>
                    <Grid>
                        Key: {car.yard?.key || 'unknown'}
                    </Grid>
                </Grid>
            </Box>

            {!place && (car.status === StockStatus.Pending || car.status === StockStatus.Ready) &&
                <Box textAlign="center" mb={2}>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setPlace('yard')}
                        sx={{width: 130, mr: 2}}
                    >
                        {t('in_yard')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setPlace('vessel')}
                        sx={{width: 130}}
                    >
                        {t('on_vessel')}
                    </Button>
                </Box>
            }

            {place &&
                <>
                    <Panes
                        car={car}
                        place={place}
                        texts={texts}
                        photos={newPhotos}
                        setTexts={setTexts}
                        setPhotos={setNewPhotos}
                    />

                    {isMobile ? (
                        <Fab
                            color="primary"
                            aria-label="add car"
                            sx={{
                                position: "fixed",
                                bottom: theme => theme.spacing(2),
                                right: theme => theme.spacing(2)
                            }}
                            onClick={save}
                        >
                            <Save/>
                        </Fab>
                    ) : (
                        <DialogActions>
                            <Button onClick={save}>{t('upload')}</Button>
                        </DialogActions>
                    )}
                </>
            }
        </>
    );
}

type PanesProps = {
    car: CarModel,
    place: keyof typeof albumsByPlace,
    texts: { [p: string]: string },
    setTexts: React.Dispatch<React.SetStateAction<{ [p: string]: string }>>,
    photos: { [p: string]: File[] },
    setPhotos: React.Dispatch<React.SetStateAction<{ [p: string]: File[] }>>,
};

const Panes = ({car, place, texts, photos, setTexts, setPhotos}: PanesProps) => {
    const albums = albumsByPlace[place];

    if (albums.length === 1) {
        const name = `${place}_${albums[0]}`;

        return (
            <BasePane
                carId={car.id}
                name={name}
                type={albums[0]}
                text={texts[name]}
                photos={photos[name]}
                setText={val => setTexts(old => ({...old, [name]: val}))}
                setPhotos={photos => setPhotos(old => ({...old, [name]: [...old[name], ...photos]}))}
            />
        );
    } else {
        return (
            <PhotoTabs
                car={car}
                place={place}
                albums={albums}
                texts={texts}
                photos={photos}
                setTexts={setTexts}
                setPhotos={setPhotos}
            />
        )
    }
};

type BasePaneProps = {
    name: string,
    carId: number,
    type: string,
    text: string,
    photos: File[],
    setText: (v: string) => void,
    setPhotos: (v: File[]) => void,
    altText?: ReactNode,
}

const BasePane = ({carId, photos, setPhotos, setText, text, name, type, altText}: BasePaneProps) => {
    const {t} = useTranslation('cars');
    const {addPhotos} = useCarPhotos();
    const {enqueueSnackbar} = useSnackbar();

    return (
        <>
            <Box px={2}>
                <TextareaAutosize
                    minRows={5}
                    placeholder={`${type} on ${name.split('_')[0]}`}
                    value={text || ''}
                    onChange={e => setText(e.target.value)}
                    style={{width: '100%'}}
                />
            </Box>

            {altText}

            <AppPhotoButtons
                addPhotos={setPhotos}
                addPhoto={p => addPhotos(carId, [{
                    name: name,
                    text: text,
                    photos: [p],
                }])
                    .then(() => enqueueSnackbar(t('uploaded'), {variant: 'success', autoHideDuration: 1000}))
                }
            />

            <Album items={photos}/>
        </>
    )
};

type PhotoTabsProps = {
    car: CarModel,
    place: keyof typeof albumsByPlace,
    albums: (keyof typeof tabLabelByAlbum)[],
    texts: { [p: string]: string },
    photos: { [p: string]: File[] },
    setTexts: React.Dispatch<React.SetStateAction<{ [p: string]: string }>>,
    setPhotos: React.Dispatch<React.SetStateAction<{ [p: string]: File[] }>>,
}

const PhotoTabs = ({albums, car, photos, place, setPhotos, setTexts, texts}: PhotoTabsProps) => {
    const {index, setIndex, setSwiper} = useSwiperTabs(0);
    const id = 'photoAdder';

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: 200,
            }}
        >
            <Box sx={{bgcolor: 'background.paper'}}>
                <Tabs
                    value={index}
                    onChange={(e, v) => setIndex(v)}
                    variant="fullWidth"
                >
                    {albums.map((a, i) => (
                        <Tab
                            key={a}
                            label={`${a === 'note' ? place : ''} ${tabLabelByAlbum[a]}`}
                            {...a11yProps(id, i)}
                        />
                    ))}
                </Tabs>
            </Box>

            <TabPanels
                active={index}
                id={id}
                setIndex={setIndex}
                setSwiper={setSwiper}
                tabs={albums.map(a => {
                    const name = `${place}_${a}`;
                    return (
                        <BasePane
                            key={a}
                            name={name}
                            type={a}
                            carId={car.id}
                            text={texts[name]}
                            photos={photos[name]}
                            setText={val => setTexts(old => ({...old, [name]: val}))}
                            setPhotos={photos => setPhotos(old => ({...old, [name]: [...old[name], ...photos]}))}
                            altText={a === 'cargo' ? (
                                <Box>
                                    {car.innerCargo.map((a, i) => (
                                        <Box key={i}>{a.quantity} &times; {a.name}, {a.weight}kg</Box>
                                    ))}
                                </Box>
                            ) : null}
                        />
                    )
                })}
            />
        </Box>
    );
};

type AlbumProps = {
    items: File[],
}

const Album = ({items}: AlbumProps) => {
    return (
        <ImageList variant="masonry" cols={4}>
            {items.map((p, i) => (
                <ImageListItem key={i}>
                    <img src={URL.createObjectURL(p)} alt=""/>
                </ImageListItem>
            ))}
        </ImageList>
    )
};

type PhotoButtonProps = {
    addPhotos: (photos: File[]) => void,
    addPhoto: (photos: File) => void,
}

const AppPhotoButtons = ({addPhoto, addPhotos}: PhotoButtonProps) => {
    return (
        <Box textAlign="center">
            <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                size="large"
            >
                <input
                    hidden
                    accept="image/*"
                    type="file"
                    multiple
                    onChange={e => {
                        if (!e.target.files)
                            return;

                        Promise.all(Array.from(e.target.files).map(resizeImage)).then(addPhotos);

                        e.target.value = '';
                    }}
                />
                <AddPhotoAlternate fontSize="large"/>
            </IconButton>
            {' '}
            {(!isIOS || isSafari) && <PhotoMaker onFinish={addPhoto} inline/>}
            {(!isIOS || isSafari) && <WebCamera onFinish={addPhoto} inline/>}
        </Box>
    )
};

export default PhotoAdder;
